/**
 * FixLocationModal - Fix suspicious client locations
 *
 * Redesigned layout:
 * - Side-by-side: Map (60%) | Address form (40%)
 * - Draggable marker on map
 * - Service region polygons displayed
 * - Live feedback on service area status
 */

import { useState, useEffect, useCallback } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Button } from '../ui/Button';
import { api, type SuspiciousDataClient, type Client } from '../../lib/api';
import { LocationMapPicker } from './LocationMapPicker';
import { useServiceRegions, pointInPolygon } from '../../hooks/useServiceRegions';
import { locationOutcomeOf, pinNotLockedIn } from './fixLocationOutcome';
import { fixLocationSaveBody } from './fixLocationSave';

// =============================================================================
// TYPES
// =============================================================================

interface FixLocationModalProps {
  isOpen: boolean;
  onClose: () => void;
  client: SuspiciousDataClient;
  /** Current client data with address/coordinates */
  clientData?: Client;
  /** Branch ID for service regions (default: 'ssf') */
  branchId?: string;
  onFixed?: () => void;
}

interface AddressFields {
  address1: string;
  address2: string;
  city: string;
  state: string;
  zip: string;
}

// =============================================================================
// COMPONENT
// =============================================================================

export function FixLocationModal({
  isOpen,
  onClose,
  client,
  clientData,
  branchId = 'ssf',
  onFixed,
}: FixLocationModalProps) {
  const queryClient = useQueryClient();

  // Form state
  const [address, setAddress] = useState<AddressFields>({
    address1: '',
    address2: '',
    city: '',
    state: '',
    zip: '',
  });
  const [latitude, setLatitude] = useState<number | null>(null);
  const [longitude, setLongitude] = useState<number | null>(null);
  // A person put the pin where it is (dragged the marker or typed a coordinate) and no lookup has replaced it since.
  // Sent with Save so the server marks the pin as theirs even when it equals the stored one (a retry after PIN_NOT_LOCKED).
  const [pinPlacedByHand, setPinPlacedByHand] = useState(false);
  // The last Save stored the pin but could not mark it as the person's (PIN_NOT_LOCKED). It survives the screen resetting
  // itself to the re-read client (whose pin now equals the one just saved), so the retry still says "placed by hand".
  const [lockRetryPending, setLockRetryPending] = useState(false);
  // What the server said when it refused or warned (it answers 200/4xx as data, it does not throw)
  const [geocodeProblem, setGeocodeProblem] = useState<string | null>(null);
  const [saveNotice, setSaveNotice] = useState<{ kind: 'failed' | 'warning'; message: string } | null>(null);

  // Fetch service regions for this branch
  const { data: regionsData, isLoading: regionsLoading } = useServiceRegions(branchId);
  const serviceRegions = regionsData?.regions || [];

  // Initialize form when modal opens
  useEffect(() => {
    if (isOpen && clientData) {
      const addr = clientData.address || {};
      setAddress({
        address1: addr.address1 || '',
        address2: addr.address2 || '',
        city: addr.city || '',
        state: addr.state || '',
        zip: addr.zip || '',
      });

      // Parse coordinates
      const lat = clientData.latitude ? parseFloat(clientData.latitude) : null;
      const lng = clientData.longitude ? parseFloat(clientData.longitude) : null;
      setLatitude(isNaN(lat!) ? null : lat);
      setLongitude(isNaN(lng!) ? null : lng);
      setPinPlacedByHand(false); // the stored pin, as it was; nobody has touched it yet
    }
  }, [isOpen, clientData]);

  // Check if location is inside service area
  const isInsideServiceArea = useCallback(() => {
    if (latitude === null || longitude === null || serviceRegions.length === 0) {
      return null;
    }
    return serviceRegions.some((region) =>
      pointInPolygon(latitude, longitude, region.polygon)
    );
  }, [latitude, longitude, serviceRegions]);

  // Handle location change from map
  const handleLocationChange = useCallback((lat: number, lng: number) => {
    setLatitude(lat);
    setLongitude(lng);
    setPinPlacedByHand(true);
  }, []);

  // Re-geocode mutation
  const geocodeMutation = useMutation({
    mutationFn: async () => {
      setGeocodeProblem(null);
      // First update the address
      const updated = await api.clients.update(client.id, { address });
      // If that was refused (rate limit, branch, ...) there is nothing to look up: say so instead of looking up the OLD address
      if (!updated.success) return updated as unknown as Awaited<ReturnType<typeof api.clients.geocodeOne>>;
      // Then re-geocode
      return api.clients.geocodeOne(client.id);
    },
    onSuccess: (response) => {
      if (response.success && response.data) {
        setLatitude(response.data.latitude);
        setLongitude(response.data.longitude);
        setPinPlacedByHand(false); // a lookup's pin is not the person's
        setLockRetryPending(false);
        return;
      }
      const outcome = locationOutcomeOf(response, 'Geocoding failed. Try adjusting the address or manually position the marker.');
      if (outcome.kind === 'failed') setGeocodeProblem(outcome.message);
    },
  });

  // Save mutation
  const saveMutation = useMutation({
    mutationFn: () => {
      setSaveNotice(null);
      return api.clients.update(client.id, fixLocationSaveBody({ address, latitude, longitude, pinPlacedByHand: pinPlacedByHand || lockRetryPending }));
    },
    onSuccess: (response) => {
      // Saved but not marked as the person's: the next Save must say again that they placed it (the coordinates then equal
      // the stored pin, which on its own looks like an echo). A refusal changes nothing; any other success ends the retry.
      if (response.success) setLockRetryPending(pinNotLockedIn(response));
      const outcome = locationOutcomeOf(response, 'Failed to save location. Please try again.');
      if (outcome.kind === 'failed') {
        // Refused (not an exception): keep the screen open and say why. It used to close as if it had saved.
        setSaveNotice({ kind: 'failed', message: outcome.message });
        return;
      }
      queryClient.invalidateQueries({ queryKey: ['dashboard'] });
      if (outcome.kind === 'warning') {
        // Saved, but the person must know something: keep the screen open to show it.
        setSaveNotice({ kind: 'warning', message: outcome.message });
        return;
      }
      onFixed?.();
      handleClose();
    },
  });

  const handleClose = () => {
    setGeocodeProblem(null);
    setSaveNotice(null);
    setAddress({ address1: '', address2: '', city: '', state: '', zip: '' });
    setLatitude(null);
    setLongitude(null);
    setPinPlacedByHand(false);
    setLockRetryPending(false);
    onClose();
  };

  const handleAddressChange = (field: keyof AddressFields, value: string) => {
    setAddress((prev) => ({ ...prev, [field]: value }));
  };

  const hasAddress = address.address1.trim() && address.city.trim() && address.state.trim();
  const hasCoordinates = latitude !== null && longitude !== null;
  const locationStatus = isInsideServiceArea();

  // Get service area name for status message
  const getServiceAreaName = () => {
    if (latitude === null || longitude === null) return '';
    for (const region of serviceRegions) {
      if (pointInPolygon(latitude, longitude, region.polygon)) {
        return region.name;
      }
    }
    return '';
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-end sm:items-center justify-center">
      <div
        className="bg-white w-full sm:w-[900px] sm:rounded-2xl sm:max-h-[90vh] max-h-[95vh] overflow-hidden flex flex-col animate-slide-up"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="bg-forest-800 text-white px-6 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="font-display text-lg font-semibold">Fix Suspicious Location</h2>
              <p className="text-sm text-white/70 mt-0.5">{client.clientName}</p>
            </div>
            <button
              onClick={handleClose}
              className="p-1 hover:bg-white/10 rounded-lg transition-colors"
              aria-label="Close"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Content - Side by Side Layout */}
        <div className="flex-1 overflow-hidden flex flex-col sm:flex-row">
          {/* Map Section (60%) */}
          <div className="w-full sm:w-3/5 h-64 sm:h-auto p-4 sm:pr-2">
            {regionsLoading ? (
              <div className="h-full flex items-center justify-center bg-sage-100 rounded-xl">
                <span className="text-sage-500">Loading map...</span>
              </div>
            ) : (
              <LocationMapPicker
                latitude={latitude}
                longitude={longitude}
                onLocationChange={handleLocationChange}
                serviceRegions={serviceRegions}
                height="100%"
              />
            )}
          </div>

          {/* Form Section (40%) */}
          <div className="w-full sm:w-2/5 overflow-y-auto px-4 sm:pl-2 sm:pr-4 py-4 space-y-4">
            {/* Issue Info */}
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-3">
              <div className="flex items-center gap-2">
                <span className="text-amber-600">
                  {client.issueType === 'missing_geocode' ? '📍' : '🗺️'}
                </span>
                <p className="text-sm font-medium text-amber-800">
                  {client.issueType === 'missing_geocode'
                    ? 'Missing coordinates'
                    : 'Outside service area'}
                </p>
              </div>
            </div>

            {/* Address Fields */}
            <div className="space-y-3">
              <label className="block text-sm font-medium text-forest-700">Address</label>
              <input
                type="text"
                value={address.address1}
                onChange={(e) => handleAddressChange('address1', e.target.value)}
                placeholder="123 Main Street"
                className="w-full px-3 py-2 rounded-xl border-2 border-sage-200 focus:border-primary-500 focus:outline-none text-sm"
              />
              <input
                type="text"
                value={address.address2}
                onChange={(e) => handleAddressChange('address2', e.target.value)}
                placeholder="Suite 100 (optional)"
                className="w-full px-3 py-2 rounded-xl border-2 border-sage-200 focus:border-primary-500 focus:outline-none text-sm"
              />
              <div className="grid grid-cols-6 gap-2">
                <input
                  type="text"
                  value={address.city}
                  onChange={(e) => handleAddressChange('city', e.target.value)}
                  placeholder="City"
                  className="col-span-3 px-3 py-2 rounded-xl border-2 border-sage-200 focus:border-primary-500 focus:outline-none text-sm"
                />
                <input
                  type="text"
                  value={address.state}
                  onChange={(e) => handleAddressChange('state', e.target.value.toUpperCase().slice(0, 2))}
                  placeholder="CA"
                  maxLength={2}
                  className="col-span-1 px-2 py-2 rounded-xl border-2 border-sage-200 focus:border-primary-500 focus:outline-none text-sm text-center"
                />
                <input
                  type="text"
                  value={address.zip}
                  onChange={(e) => handleAddressChange('zip', e.target.value)}
                  placeholder="ZIP"
                  className="col-span-2 px-3 py-2 rounded-xl border-2 border-sage-200 focus:border-primary-500 focus:outline-none text-sm"
                />
              </div>
            </div>

            {/* Coordinates */}
            <div className="space-y-2">
              <label className="block text-sm font-medium text-forest-700">Coordinates</label>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <input
                    type="text"
                    inputMode="decimal"
                    value={latitude !== null ? latitude.toFixed(6) : ''}
                    onChange={(e) => {
                      const val = parseFloat(e.target.value);
                      setLatitude(isNaN(val) ? null : val);
                      setPinPlacedByHand(true);
                    }}
                    placeholder="Latitude"
                    className="w-full px-3 py-2 rounded-xl border-2 border-sage-200 focus:border-primary-500 focus:outline-none text-sm font-mono"
                  />
                </div>
                <div>
                  <input
                    type="text"
                    inputMode="decimal"
                    value={longitude !== null ? longitude.toFixed(6) : ''}
                    onChange={(e) => {
                      const val = parseFloat(e.target.value);
                      setLongitude(isNaN(val) ? null : val);
                      setPinPlacedByHand(true);
                    }}
                    placeholder="Longitude"
                    className="w-full px-3 py-2 rounded-xl border-2 border-sage-200 focus:border-primary-500 focus:outline-none text-sm font-mono"
                  />
                </div>
              </div>
            </div>

            {/* Re-geocode Button */}
            <Button
              variant="secondary"
              size="sm"
              onClick={() => geocodeMutation.mutate()}
              disabled={!hasAddress || geocodeMutation.isPending}
              className="w-full"
            >
              {geocodeMutation.isPending ? 'Geocoding...' : 'Re-geocode from Address'}
            </Button>

            {(geocodeMutation.isError || geocodeProblem) && (
              <p className="text-sm text-red-600">
                {geocodeProblem ?? 'Geocoding failed. Try adjusting the address or manually position the marker.'}
              </p>
            )}

            {/* Google Maps Link */}
            {hasCoordinates && (
              <a
                href={`https://www.google.com/maps?q=${latitude},${longitude}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 text-sm text-primary-600 hover:underline"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                </svg>
                Verify on Google Maps
              </a>
            )}
          </div>
        </div>

        {/* Footer with Status */}
        <div className="border-t border-sage-200 px-6 py-4">
          <div className="flex items-center justify-between gap-4">
            {/* Service Area Status */}
            <div className="flex-1">
              {hasCoordinates && locationStatus !== null && (
                <div
                  className={`flex items-center gap-2 text-sm ${
                    locationStatus ? 'text-green-700' : 'text-amber-700'
                  }`}
                >
                  {locationStatus ? (
                    <>
                      <span>✓</span>
                      <span>Location is inside {getServiceAreaName() || 'service area'}</span>
                    </>
                  ) : (
                    <>
                      <span>⚠️</span>
                      <span>Location is outside {branchId.toUpperCase()} service area</span>
                    </>
                  )}
                </div>
              )}
            </div>

            {/* Action Buttons */}
            <div className="flex gap-3">
              <Button variant="ghost" onClick={handleClose}>
                Cancel
              </Button>
              <Button
                variant="primary"
                onClick={() => saveMutation.mutate()}
                disabled={(!hasAddress && !hasCoordinates) || saveMutation.isPending}
              >
                {saveMutation.isPending ? 'Saving...' : 'Save Location'}
              </Button>
            </div>
          </div>

          {/* Error Display */}
          {saveMutation.isError && (
            <div className="mt-3 bg-red-50 border border-red-200 rounded-xl p-3">
              <p className="text-sm text-red-700">
                Failed to save location. Please try again.
              </p>
            </div>
          )}
          {saveNotice && (
            <div
              role="alert"
              className={`mt-3 rounded-xl p-3 border ${saveNotice.kind === 'failed' ? 'bg-red-50 border-red-200' : 'bg-amber-50 border-amber-200'}`}
            >
              <p className={`text-sm ${saveNotice.kind === 'failed' ? 'text-red-700' : 'text-amber-800'}`}>{saveNotice.message}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
